// Import <uo-select-option>
import './option';

// Import utilities
import { isDefined, isEmpty } from '@automator/utilities';

// Import style for web component
const styles = require( './web-component-style.scss' );

// Base template
const template = document.createElement( 'template' );
template.innerHTML = `
    <style>

        :host {
            display: inline-flex;
        }
        
        ${styles}

    </style>

    <button class="select">
    	<div class="select-button"></div>
    	<div class="select-options"></div>
    </button>
`;

/**
 * Select <uo-select>
 *
 * Attributes:
 */
class AutomatorSelect extends HTMLElement {
	/**
	 * Defines observed attributes
	 *
	 * @return {Array} Observed attributes
	 */
	static get observedAttributes() {
		return [];
	}

	/**
	 * Creates the component
	 *
	 * @return {undefined}
	 */
	constructor() {
		super();

		// Attach shadow
		this.attachShadow( {
			mode: 'open',
		} );

		// Create alert
		this.shadowRoot.appendChild( template.content.cloneNode( true ) );
	}

	/**
	 * Callback invoked when the component is added to the DOM
	 *
	 * @return {undefined}
	 */
	connectedCallback() {
		this.shadowRoot.querySelector( '.select-options' )
			.append( ...this.querySelectorAll( ':scope > uo-select-option' ) );

		// Handle open/close
		this.#handleOpen();

		// Render the selected value
		this.#renderSelectedOption();

		// Listen clicks on the options
		this.#listenClicksOnOptions();
	}
	
	/**
	 * Handle open/close of the select
	 * We have this approach beacuse focus-within isn't reliable in Safari
	 * https://bugs.webkit.org/show_bug.cgi?id=22261
	 */
	#handleOpen() {
		// Listen clicks to the select open
		this.$button.addEventListener( 'click', () => {
			// Check if it's already open
			if ( this.$select.classList.contains( 'select--open' ) ) {
				return;
			}

			// Open the select
			this.$select.classList.add( 'select--open' );

			// Listen clicks outside
			const listenClicksOutsideSelect = ( event ) => {
				// Check if a click inside
				if ( this.contains( event.target ) ) {
					return;
				}

				// Close select
				this.$select.classList.remove( 'select--open' );

				// Stop listening clicks outside
				document.removeEventListener( 'click', listenClicksOutsideSelect );
			}

			setTimeout( () => {
				document.addEventListener( 'click', listenClicksOutsideSelect );
			}, 100 );
		} );
	}

	/**
	 * Listens clicks on the options
	 *
	 * @return {undefined}
	 */
	#listenClicksOnOptions() {
		// Iterate options
		this.$options.forEach( ( $option ) => {
			// Add event listener
			$option.addEventListener( 'click', () => {
				// Select the option
				this.value = $option.value;
			} );
		} );
	}

	/**
	 * Sets a new value
	 *
	 * @param {String} newValue The value
	 * @return {undefined}
	 */
	set value( newValue ) {
		// Check if the value is valid
		if ( this.optionExists( newValue ) ) {
			// Check if the new value is different
			if ( this.value != newValue ) {
				// Unselect all options
				// Iterate all options
				this.$options.forEach( ( $option ) => {
					// Unselect option
					$option.selected = false;
				} );

				// Select the new option
				this.#selectOption( newValue );

				// Render the selected option
				this.#renderSelectedOption();

				// Trigger change event
				this.dispatchEvent(
					new Event( 'change', {
						bubbles: true,
						composed: true,
					} )
				);

				// Close dropdown
				document.body.click();
			}
		}
	}

	/**
	 * Returns the value
	 *
	 * @return {String} The value
	 */
	get value() {
		// Default value
		let value = '';

		// Check if there are options
		if ( this.hasOptions ) {
			// Iterate options
			this.$options.forEach( ( $option ) => {
				// Searchs for the one selected
				if ( $option.selected ) {
					value = $option.value;
				}
			} );

			// Check if we still don't have a value
			if ( isEmpty( value ) ) {
				// Use the value of the first option
				value = this.$options[0].value;
			}
		}

		return value;
	}

	/**
	 * Checks whether the select has options
	 *
	 * @return {Boolean} True if it has options
	 */
	get hasOptions() {
		return ! isEmpty( this.$options );
	}

	/**
	 * Renders the selected option
	 *
	 * @return {undefined}
	 */
	#renderSelectedOption() {
		// Get current selected option
		const $option = this.getOptionById( this.value );

		// Add content to the button
		this.$button.innerText = $option.text;
	}

	/**
	 * Searchs an option using the value
	 *
	 * @param {String} optionValue The option value
	 * @return {Node}               The option node
	 */
	getOptionById( optionValue ) {
		// Default response
		let $response = null;

		// Iterate options
		this.$options.forEach( ( $option ) => {
			// Compare the value
			if ( $option.value == optionValue ) {
				// Exists, thus is valid
				$response = $option;
			}
		} );

		return $response;
	}

	/**
	 * Check if a value is valid by checking if the option exists
	 *
	 * @param {String} optionValue The value
	 * @return {Boolean}       True if the value is valid
	 */
	optionExists( optionValue ) {
		return isDefined( this.getOptionById( optionValue ) );
	}

	/**
	 * Safari doesn't set the focus or focus-within state on click
	 */
	#fixSafariFocusIssue() {
		this.$select.addEventListener( 'click', () => {
			this.$select.focus();
		} );
	}

	/**
	 * Select an option
	 *
	 * @param {String} optionValue The value of the option that has to be selected
	 * @return {undefined}
	 */
	#selectOption( optionValue ) {
		// Search for the option
		const $option = this.getOptionById( optionValue );

		// Check if it's defined
		if ( isDefined( $option ) ) {
			// Change the "selected" property
			$option.selected = true;
		}
	}

	/**
	 * Returns the select
	 *
	 * @return {Node} The select
	 */
	get $select() {
		return this.shadowRoot.querySelector( '.select' );
	}

	/**
	 * Returns all options inside the select
	 *
	 * @return {NodeList} The options
	 */
	get $options() {
		return this.shadowRoot.querySelectorAll( 'uo-select-option' );
	}

	/**
	 * Returns the select button (the one that opens the dropdown)
	 *
	 * @return {Node} The button
	 */
	get $button() {
		return this.shadowRoot.querySelector( '.select-button' );
	}
}

customElements.define( 'uo-select', AutomatorSelect );
